import { userTickerListApi, UserListPreferences } from '../services/userTickerListApi';

// Page types that support list-level hidden indicators preferences.
export type PageType = 'daily' | 'performance' | 'technicals' | 'fundamentals' | 'analysts' | 'insiders' | 'prosCons';

/**
 * Save user list preferences for a specific page type
 */
export async function saveUserListPreferences(
  userTickerListId: number,
  pageType: PageType,
  hiddenStatistics: string[]
): Promise<void> {
  try {
    // Check if preferences already exist
    let existingPreferences: UserListPreferences | null = null;
    try {
      const response = await userTickerListApi.getUserListPreferences(userTickerListId);
      existingPreferences = response.data; // This will be null if no preferences exist
    } catch (error) {
      // Preferences don't exist yet, we'll create them
      console.log('No existing preferences found, will create new ones');
    }

    // Prepare the update data based on page type
    const updateData: any = {};
    switch (pageType) {
      case 'daily':
        updateData.dailyIndicatorsToHide = hiddenStatistics;
        break;
      case 'performance':
        updateData.performanceIndicatorsToHide = hiddenStatistics;
        break;
      case 'technicals':
        updateData.technicalIndicatorsToHide = hiddenStatistics;
        break;
      case 'fundamentals':
        updateData.fundamentalIndicatorsToHide = hiddenStatistics;
        break;
      case 'analysts':
        updateData.analystIndicatorsToHide = hiddenStatistics;
        break;
      case 'insiders':
        updateData.insiderIndicatorsToHide = hiddenStatistics;
        break;
      case 'prosCons':
        updateData.prosConsIndicatorsToHide = hiddenStatistics;
        break;
    }

    if (existingPreferences) {
      // Update existing preferences
      await userTickerListApi.updateUserListPreferences(userTickerListId, updateData);
      console.log(`✅ Updated ${pageType} preferences for list ${userTickerListId}`);
    } else {
      // Create new preferences
      await userTickerListApi.createUserListPreferences({
        userTickerListId,
        ...updateData
      });
      console.log(`✅ Created ${pageType} preferences for list ${userTickerListId}`);
    }
  } catch (error) {
    console.error(`❌ Failed to save ${pageType} preferences:`, error);
    throw error;
  }
}

/**
 * Load user list preferences for a specific page type
 */
export async function loadUserListPreferences(
  userTickerListId: number,
  pageType: PageType
): Promise<string[]> {
  try {
    const response = await userTickerListApi.getUserListPreferences(userTickerListId);
    const preferences = response.data;

    // Handle null preferences data
    if (!preferences) {
      console.log(`No preferences found for ${pageType} page`);
      return [];
    }

    switch (pageType) {
      case 'daily':
        return preferences.dailyIndicatorsToHide || [];
      case 'performance':
        return (preferences as any).performanceIndicatorsToHide || [];
      case 'technicals':
        return preferences.technicalIndicatorsToHide || [];
      case 'fundamentals':
        return preferences.fundamentalIndicatorsToHide || [];
      case 'analysts':
        return preferences.analystIndicatorsToHide || [];
      case 'insiders':
        return preferences.insiderIndicatorsToHide || [];
      case 'prosCons':
        return (preferences as any).prosConsIndicatorsToHide || [];
      default:
        return [];
    }
  } catch (error) {
    console.log(`No preferences found for ${pageType} page`);
    return [];
  }
}
